import { useCallback, useEffect, useMemo, useState } from 'react';
import { CaregiverRoleEnum, ConsultationTypeEnum } from '@/shared/enums';
import { FetchError } from '@/shared/fetch';
import { Log } from '@/shared/Log';
import {
    bookingDraftAtom,
    BookingPage,
    hasBlockingConsultationAtom,
    resetDraftAtom,
    SessionStorageKeys,
    updateDraftAtom,
} from '@/features/booking/shared/model/bookingState';
import {
    CaregiverSelectionStatus,
    InsurancePolicyProviderDto,
    IPetDto,
    IUserDto,
    ProductDto,
    SelectChildDto,
} from '@/shared/interfaces';
import { isPhoneNumber } from '@/shared/validators';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { usePreferredCaregiver } from './usePreferredCaregiver';
import {
    QueryKeys,
    UrlState,
    urlStateAtom,
    parseConsultationType,
} from '@/features/booking/shared/model/urlState';
import { updateUserForQuestionnaire } from '@/features/booking/shared/api/updateUserForQuestionnaire';
import { fetchHasBlockingConsultation } from '@/features/booking/shared/api/fetchHasBlockingConsultation';
import { fetchVisibleConsultationTypes } from '@/features/booking/shared/api/fetchVisibleConsultationTypes';
import { usePreloadAzureVideo } from '@/features/booking/shared/model/usePreloadAzureVideo';

// Re-export for convenience
export { parseConsultationType };

export interface BookingStep {
    page: BookingPage;
    isEnabled: (state: UrlState) => boolean;
}

export const useBooking = (
    initialProducts: ProductDto[],
    user: IUserDto,
    initialChildren: SelectChildDto[],
    insuranceProviderDto: InsurancePolicyProviderDto,
) => {
    const [isLoading, setIsLoading] = useState(true);
    const [children, setChildren] = useState<SelectChildDto[]>(initialChildren);
    const [productOptions, setProductOptions] =
        useState<ProductDto[]>(initialProducts);

    const [urlState, updateUrl] = useAtom(urlStateAtom);
    const resetSessionStorageState = useSetAtom(resetDraftAtom);
    const draftState = useAtomValue(bookingDraftAtom);
    const updateDraftState = useSetAtom(updateDraftAtom);
    const [_, setBlockingConsultation] = useAtom(hasBlockingConsultationAtom);

    const chosenProduct = useMemo(() => {
        if (!urlState.consultationType) return null;

        const product = productOptions.find(
            (p) =>
                p.ConsultationType ===
                parseConsultationType(urlState.consultationType),
        );

        return product ?? null;
    }, [productOptions, urlState.page, urlState.consultationType]);

    const getChosenProduct = (state: UrlState) => {
        const product = productOptions.find(
            (p) =>
                p.ConsultationType ===
                parseConsultationType(state.consultationType),
        );
        return product ?? null;
    };

    const { caregivers: availableCaregivers, hasPreferredCaregiver } =
        usePreferredCaregiver({
            consultationType: chosenProduct?.ConsultationType,
            performInitialFetch:
                !!chosenProduct &&
                chosenProduct.CaregiverSelectionStatus !==
                    CaregiverSelectionStatus.Disabled,
        });

    usePreloadAzureVideo(chosenProduct?.IsVideo);

    const steps: BookingStep[] = useMemo(
        () => [
            {
                page: BookingPage.SelectPatient,
                isEnabled: (state) =>
                    initialProducts.some((p) => p.ChildrenSelectionEnabled) &&
                    !state.isPatientPreselected,
            },
            {
                page: BookingPage.SelectPet,
                isEnabled: (state) =>
                    productOptions[0]?.HandledBy ===
                        CaregiverRoleEnum.Veterinarian && !state.petId,
            },
            {
                page: BookingPage.SelectProduct,
                isEnabled: () =>
                    initialProducts.length > 1 ||
                    productOptions.some(
                        (p) =>
                            p.Price.Price > 0 &&
                            !p.NeverShowMissingCoverageWarning,
                    ),
            },
            {
                page: BookingPage.Disclaimer,
                isEnabled: (state) => {
                    return !!getChosenProduct(state)?.Texts?.Disclaimer;
                },
            },
            {
                page: BookingPage.SelectCategory,
                isEnabled: (state) => {
                    const product = getChosenProduct(state);
                    const hasCategories =
                        product?.AvailableCategories != null &&
                        product.AvailableCategories.Categories.length > 0;
                    const skipCategoryQuestion =
                        insuranceProviderDto?.SkipCategoryQuestion &&
                        productOptions[0].HandledBy ===
                            CaregiverRoleEnum.Physiotherapist;
                    return hasCategories && !skipCategoryQuestion;
                },
            },
            {
                page: BookingPage.NotifyOwnDoctor,
                isEnabled: (state) => {
                    return (
                        getChosenProduct(state)?.HandledBy !=
                        CaregiverRoleEnum.Veterinarian
                    );
                },
            },
            {
                page: BookingPage.DescribeProblem,
                isEnabled: (state) => {
                    return !getChosenProduct(state)?.SkipSubjectForm;
                },
            },
            {
                page: BookingPage.Phone,
                isEnabled: () => true,
            },
            {
                page: BookingPage.SelectCaregiver,
                isEnabled: (state) => {
                    const selectionStatus =
                        getChosenProduct(state)?.CaregiverSelectionStatus;
                    return (
                        selectionStatus ===
                            CaregiverSelectionStatus.ForceSelection &&
                        availableCaregivers.length > 1 &&
                        !hasPreferredCaregiver
                    );
                },
            },
            {
                page: BookingPage.BookOrQueueUp,
                isEnabled: (state) => {
                    return getChosenProduct(state)?.IsVideo;
                },
            },
        ],
        [
            productOptions,
            chosenProduct,
            availableCaregivers,
            hasPreferredCaregiver,
        ],
    );

    const isStepValid = (page: BookingPage, state: UrlState): boolean => {
        switch (page) {
            case BookingPage.SelectPatient:
                return state.patientUid != null;
            case BookingPage.SelectPet:
                return state.petId != null;
            case BookingPage.SelectProduct:
                return state.consultationType != null;
            case BookingPage.Disclaimer:
                return true;
            case BookingPage.SelectCategory:
                const categories =
                    chosenProduct?.AvailableCategories?.Categories ?? [];
                const categoryIds = categories.map((x) => x.Id);
                return (
                    draftState?.category != null &&
                    categoryIds.includes(draftState?.category)
                );
            case BookingPage.NotifyOwnDoctor:
                return state.notifyOwnDoctor != null;
            case BookingPage.DescribeProblem:
                const hasSubject = !!draftState?.subject;
                const hasSubjectAndMessage =
                    hasSubject && !!draftState?.message;
                const hasAttachment = !!draftState?.attachmentUids;

                if (chosenProduct?.IsAttachmentsRequired && !hasAttachment)
                    return false;

                return chosenProduct?.IsVideo
                    ? hasSubject
                    : hasSubjectAndMessage;
            case BookingPage.Phone:
                return isPhoneNumber(draftState?.phone);
            case BookingPage.SelectCaregiver:
                return draftState?.preferredCaregiverUid != null;

            default:
                return false;
        }
    };

    const bootstrap = useCallback(async () => {
        // if page is in query (page refresh), no bootstrapping is necessary
        if (urlState[QueryKeys.Page]) {
            setTimeout(() => setIsLoading(false), 1000);
            return;
        }

        // no page in query = not a refresh => wipe session storage
        resetSessionStorageState();
        const updatedUrlState = { ...urlState };

        // set product if we only have one to choose from
        if (initialProducts.length === 1) {
            updatedUrlState[QueryKeys.ConsultationType] =
                ConsultationTypeEnum[initialProducts[0].ConsultationType];
        }

        // handle patient pre-selection
        const isChildrenSelectionDisabled = !initialProducts.some(
            (p) => p.ChildrenSelectionEnabled,
        );
        const preselectedPatientUid =
            urlState[QueryKeys.PatientUid] ??
            (isChildrenSelectionDisabled ? user.UID : null);

        if (preselectedPatientUid) {
            updatedUrlState[QueryKeys.IsPatientPreselected] = true.toString();
            const hasBlocking = await onSelectPatient(
                preselectedPatientUid,
                true,
                updatedUrlState,
            );
            if (hasBlocking) {
                setTimeout(() => setIsLoading(false), 1000);
                return;
            }
        }

        // finally, determine which step should be first step
        const enabledSteps = steps.filter((s) => s.isEnabled(updatedUrlState));
        let initialStep: BookingStep = enabledSteps[0];

        // this handles cases where the consultation is created solely to keep track of questionnaire (e.g., sundvaegt followup)
        if (updatedUrlState[QueryKeys.SkipBookingFlow] === true.toString()) {
            initialStep = enabledSteps.at(-1);
        }

        updatedUrlState[QueryKeys.Page] = BookingPage[initialStep?.page];
        updateUrl(updatedUrlState, true);

        setTimeout(() => setIsLoading(false), 1000);
    }, [urlState]);

    useEffect(() => {
        bootstrap();
    }, []);

    useEffect(() => {
        if (!chosenProduct) return;

        if (
            chosenProduct.RequiredQuestionnaire != null &&
            urlState.submissionId == null
        ) {
            window.location.href = `/questionnaire/v2/${chosenProduct.RequiredQuestionnaire}`;
            return;
        }

        if (!urlState.submissionId) return;
        if (urlState[QueryKeys.UserIdAttached] === true.toString()) return;

        void updateUserForQuestionnaire(urlState.submissionId);
        updateUrl({ ...urlState, userIdAttached: true.toString() }, true);
    }, [chosenProduct, urlState.submissionId, urlState.userIdAttached]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [urlState.page]);

    const navigateBack = () => {
        window.history.back();
        return;
    };

    const navigateForward = (
        updatedUrlState: UrlState = null,
        replace: boolean = false,
    ) => {
        const state = updatedUrlState ?? urlState;

        const currentIndex = steps.findIndex(
            (s) =>
                s.page === BookingPage[state.page as keyof typeof BookingPage],
        );
        const currentStep = steps[currentIndex];

        if (!isStepValid(currentStep.page, state)) {
            return;
        }

        const nextStep = steps
            .slice(currentIndex + 1)
            .find((s) => s.isEnabled(state));

        if (nextStep === undefined) {
            Log.error(
                `Next step was undefined in navigateForward().
                Navigating from: ${currentStep.page}`,
                { ...urlState, draftState },
            );
            return;
        }

        updateUrl(state, true); // replace state, to enable pre-selection when navigating back
        const finalStateWithNextPage = {
            ...state,
            page: BookingPage[nextStep.page],
        };
        updateUrl(finalStateWithNextPage, replace);
    };

    const hasBlockingConsultation = async (
        product: ProductDto,
        patientUid: string = null,
    ) => {
        if (!product.IsVideo) return false;

        const uid = patientUid ?? urlState[QueryKeys.PatientUid] ?? user.UID;
        try {
            return await fetchHasBlockingConsultation(
                uid,
                product.ConsultationType,
            );
        } catch (err: any) {
            if ((err as FetchError<any>).status === 401) {
                window.location.reload();
                return;
            }
            Log.error('Error fetching upcoming consultations', null, err);
        }
    };

    // returns boolean indicating if user has a blocking consultation
    const onSelectPatient = async (
        patientUid: string,
        skipNavigation: boolean = false,
        state: UrlState = null,
    ): Promise<boolean> => {
        const product = chosenProduct ?? getChosenProduct(state ?? urlState);

        if (product) {
            const hasBlocking = await hasBlockingConsultation(
                product,
                patientUid,
            );
            if (hasBlocking) {
                setBlockingConsultation(true);
                return true;
            }
        }

        const updatedUrlState: UrlState = { ...urlState, patientUid };

        // filter products based on selected patient
        const isParentChosen = patientUid === user.UID;
        updateDraftState({
            [SessionStorageKeys.IsParentChosen]: isParentChosen.toString(),
        });

        const needsStartupCheckupDistinction =
            !urlState[QueryKeys.ForceConsultationType] &&
            productOptions.some((p) => p.RequiresRecentConsultationsCheck);
        const visibleTypes = needsStartupCheckupDistinction
            ? await fetchVisibleConsultationTypes(
                  productOptions[0].HandledBy,
                  patientUid,
              )
            : null;

        const updatedProductOptions = initialProducts.filter((p) => {
            if (!isParentChosen && !p.ChildrenSelectionEnabled) return false;

            const isWrongProductForPatient =
                p.RequiresRecentConsultationsCheck &&
                visibleTypes != null &&
                !visibleTypes.includes(p.ConsultationType);
            return !isWrongProductForPatient;
        });

        setProductOptions(updatedProductOptions);

        if (skipNavigation) return false;

        navigateForward(updatedUrlState);
        return false;
    };

    const onSelectPet = (pet: IPetDto) => {
        const updatedUrlState: UrlState = { ...urlState, petId: pet.Id };
        updateDraftState({
            [SessionStorageKeys.OriginalPetWeight]: pet.WeightKg?.toString(),
        });
        navigateForward(updatedUrlState);
    };

    const onSelectProduct = async (product: ProductDto) => {
        const hasBlocking = await hasBlockingConsultation(product);
        if (hasBlocking) {
            setBlockingConsultation(true);
            return;
        }

        const updatedUrlState: UrlState = {
            ...urlState,
            consultationType: ConsultationTypeEnum[product.ConsultationType],
        };
        navigateForward(updatedUrlState);
    };

    const onAcceptDisclaimer = async () => {
        navigateForward();
    };

    const onSelectNotifyOwnDoctor = (accepted: boolean) => {
        const updatedUrlState: UrlState = {
            ...urlState,
            notifyOwnDoctor: accepted.toString(),
        };
        navigateForward(updatedUrlState);
    };

    const onSubmitDescribeProblem = () => {
        navigateForward();
    };

    const onSubmitPhone = () => {
        navigateForward();
    };

    const onSubmitCategory = () => {
        navigateForward();
    };

    const onSubmitPreferredCaregiver = () => {
        // replace=true is to avoid showing SelectCaregiver step again after completion (back navigation)
        navigateForward(null, true);
    };

    return {
        productOptions,
        children,
        setChildren,
        chosenProduct,
        currentPage: BookingPage[urlState.page as keyof typeof BookingPage],
        caregiverTitle:
            initialProducts[0]?.Texts?.CaregiverName?.toLowerCase() ?? '',
        navigateBack,
        onSelectPatient,
        onSelectPet,
        onSelectProduct,
        onAcceptDisclaimer,
        onSubmitCategory,
        onSelectNotifyOwnDoctor,
        onSubmitDescribeProblem,
        onSubmitPhone,
        onSubmitPreferredCaregiver,
        isLoading,
    };
};
